import React, {
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Checkbox, Form, Input, InputRef, Modal } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { InputNote, KeyboardShortcut, Row, Text } from '@ydistri/ds';
import { useSelectedTemplate } from '../../../hooks/useSelectedTemplate';
import { useGetTemplatesQuery } from '../../../apis/apiApplication';
import { getSelectedProjectShortName } from '../../../apis/apiHeader';
import {
  useCreateCalculationMutation,
  useGetCalculationsQuery,
  useLazyGetCalculationsQuery,
} from '../../Calculations/apiCalculations';
import { generatePath, useNavigate } from 'react-router';
import { ROUTES } from '../../../components/menu/getMenuLeftItems';
import { addToast, createDebugLog } from '@ydistri/utils';
import { useUser } from '../../../hooks/useUser';
import {
  CalculationMode,
  CalculationRequest,
  CalculationType,
  Operation,
  SortDirection,
  ValidationProblemCode,
} from '@ydistri/api-sdk';
import { useSelector } from 'react-redux';
import OverlappingCalculationForm, { OVERLAP_TABLE_ID } from './OverlappingCalculationForm';
import { ReduxState, useAppDispatch } from '../../../store';
import CalculationModeSelect from './CalculationModeSelect';
import MergedCalculationTemplateSelect, {
  MergedCalculationData,
} from './TemplateMultiselectAndSort/MergedCalculationTemplateSelect';
import { TemplateSelect } from '../../Configuration/TemplateSelect';
import ManualCalculationSelect, {
  ManualCalculationData,
} from '../../Calculations/common/ManualCalculationSelect';
import {
  CalculationDataType,
  CalculationInfo,
  CalculationTableParams,
} from '../../Calculations/calculationsTypes';
import { resetSelectedRowKeys } from '../../Calculations/calculationsSlice';
import { useSelectedProjectShortName } from '../../../hooks/useSelectedProjectShortName';
import { useApplicationDate } from '../../../hooks/useApplicationDate';
import { skipToken } from '@reduxjs/toolkit/query';
import { format } from 'date-fns';
import Validator from '../../Configuration/Validator/Validator';
import { createTemplateOrCalculationObject } from '../../../hooks/useTemplateOrCalculation';
import { TbArrowBigRight } from 'react-icons/tb';
import LoadingIndicator from '../../../components/global/LoadingIndicator/LoadingIndicator';
import { Overlay } from '../../../components/global/Overlay';
import { createTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';
import {
  messageFor,
  problemMessagesOf,
  problemsOfError,
} from '../../Configuration/Validator/problemsLib';
import {
  localSelectionProblems,
  nextRevisions,
  requestRows,
  rowLabel,
  RowProblem,
  RowRevisions,
  shownProblems,
  sortRefusal,
  stillRefused,
  SUBORDINATES_PATH,
  templateRowErrors,
} from './createCalculationLib';

const debugLog = createDebugLog('Configuration', 'CreateCalculationModal');

export interface CreateCalculationModalProps {
  open: boolean;
  onCancel: () => void;
  displayTemplateSelect?: boolean;
  initialValues?: FormFields;
}

interface SubordinateCalculations {
  originalCalculationId?: number;
  templateId?: number;
  title: string;
  calculationMode: CalculationMode;
  calculationType: CalculationType;
  /**
   * Required because these are sent as CalculationRequest, where the flag is non-nullable. The
   * backend propagates a merged root's choice onto its computed children by itself, so this only
   * has to be a valid value - it is not what makes the propagation work.
   */
  shouldImportDebugRows: boolean;
}

export interface FormFields {
  title: string;
  description: string;
  private: boolean;
  /** Superuser-only; the field is absent from the form for everyone else. */
  shouldImportDebugRows?: boolean;
  templateId?: number;
  calculationMode: CalculationMode;
  manualCalculations?: CalculationInfo[]; // legacy support for initialValues, will be mapped to ManualCalculationData
  mergedCalculationTemplates?: CalculationInfo[];
  overlappingCalculations?: CalculationDataType[];
}

// Local copies of two server rules, worded by the server's code so a renamed code fails here.
const titleInputRules = [
  { required: true, message: messageFor(ValidationProblemCode.CalculationTitleRequired) },
];
const templateSelectRules = [
  { required: true, message: messageFor(ValidationProblemCode.CalculationTemplateRequired) },
];
const formLayout = {
  labelCol: { span: 3 },
  wrapperCol: { span: 21 },
};

/** The server's paths on the form's own fields; subordinate paths are mapped to their rows separately. */
const fieldOfPath: Record<string, keyof FormFields> = { title: 'title', templateId: 'templateId' };

const formStyle = {
  flexGrow: 1,
};

const CreateCalculationModal: React.FC<CreateCalculationModalProps> = ({
  open,
  onCancel,
  displayTemplateSelect = false,
  initialValues,
}) => {
  const inputRef = useRef<InputRef>(null);
  const navigate = useNavigate();
  const selectedTemplate = useSelectedTemplate();
  const dispatch = useAppDispatch();
  const [getCalculationsQuery, queryStatus] = useLazyGetCalculationsQuery();
  const loading = queryStatus.isFetching;

  const applicationDate = useApplicationDate();

  const projectShortName = useSelectedProjectShortName();
  const apiProjectShortName = getSelectedProjectShortName();
  const { currentData: templates } = useGetTemplatesQuery(
    projectShortName && apiProjectShortName === projectShortName ? apiProjectShortName : skipToken,
  );
  const initialOverlappingCalculationsParams: CalculationTableParams | typeof skipToken = useMemo(
    () =>
      applicationDate
        ? {
            skip: 0,
            conditions: [
              {
                fieldName: 'FinishExecutionUntil',
                operation: Operation.Ge,
                value: format(applicationDate, 'yyyy-MM-dd'),
              },
            ],
            productionCalculationsOnly: true,
            projectShortName: projectShortName,
          }
        : skipToken,
    [applicationDate, projectShortName],
  );

  const { data: initialOverlappingCalculations } = useGetCalculationsQuery(
    initialOverlappingCalculationsParams,
  );

  const [form] = Form.useForm<FormFields>();
  const [createCalculation, createCalculationStatus] = useCreateCalculationMutation();
  const user = useUser();
  const [formHasErrors, setFormHasErrors] = useState(false);
  const [mergedCalculationData, setMergedCalculationData] = useState<MergedCalculationData>();
  /**
   * The row problems of the last refused request, remembered by the row they were about; each is
   * shown on its row wherever it moves and retires when that row is edited or removed.
   */
  const [serverRowProblems, setServerRowProblems] = useState<RowProblem[]>([]);
  /** The computed template's verdict; unanswered is not valid. */
  const [computedTemplateValid, setComputedTemplateValid] = useState<boolean>();
  const [manualCalculationsData, setManualCalculationsData] = useState<ManualCalculationData>();
  const [calculationMode, setCalculationMode] = useState<CalculationMode>(
    initialValues?.calculationMode ?? CalculationMode.Computed,
  );
  const [templateId, setTemplateId] = React.useState<number | undefined>(
    displayTemplateSelect ? undefined : (selectedTemplate?.id ?? 0),
  );
  const onTemplateIdChange = useCallback((id: number | undefined) => {
    setTemplateId(id);
    setComputedTemplateValid(undefined);
  }, []);

  /**
   * TemplateId in form is required, but we have custom component that doesn't work well with antd form.
   * We have to set form value manually every time templateId changes.
   */
  useEffect(() => {
    form.setFieldValue('templateId', templateId);
  }, [form, templateId]);

  const selectedOverlappingCalculations = useSelector(
    (state: ReduxState) => state.calculations.selectedRowKeys[OVERLAP_TABLE_ID]?.arr,
  );

  const hasInitialManualCalculations = (initialValues?.manualCalculations?.length ?? 0) > 0;

  useEffect(() => {
    if (!manualCalculationsData && initialValues?.manualCalculations) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- setManualCalculationsData also used on another place
      setManualCalculationsData({
        manualCalculationIds: initialValues.manualCalculations.map(m => m.id),
        calculationTitles: Object.fromEntries(
          initialValues.manualCalculations.map(m => [m.id, m.title]),
        ),
      });
    }
  }, [initialValues, manualCalculationsData]);

  const voidClickHandler = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  /**
   * The subordinate rows in request order, the local copies of their rules as problems with the
   * server's identity, and the reconciled list: a server answer replaces the copy with the same
   * (code, target, path), the rest stays.
   */
  const rows = useMemo(
    () => requestRows(manualCalculationsData, mergedCalculationData),
    [manualCalculationsData, mergedCalculationData],
  );
  const selectionProblems = useMemo(
    () => localSelectionProblems(calculationMode, rows),
    [calculationMode, rows],
  );
  const subordinateProblems = useMemo(
    () => shownProblems(calculationMode, rows, serverRowProblems),
    [calculationMode, rows, serverRowProblems],
  );
  // A refused row's problem retires for good when the row is edited or removed: each row carries a
  // revision that advances on those, a problem is about one revision, and a refusal that arrives
  // late is cut down to the rows and revisions as they are on arrival (the refs hold them).
  const rowsRef = useRef(rows);
  const revisionsRef = useRef<RowRevisions>({});
  useEffect(() => {
    revisionsRef.current = nextRevisions(revisionsRef.current, rowsRef.current, rows);
    rowsRef.current = rows;
    const revisions = revisionsRef.current;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the remembered problems follow the rows
    setServerRowProblems(prev => {
      const kept = stillRefused(prev, rows, revisions);
      return kept.length === prev.length ? prev : kept;
    });
  }, [rows]);
  const subordinateErrors = useMemo(
    () => templateRowErrors(subordinateProblems, rows),
    [subordinateProblems, rows],
  );

  const blockedTemplateIds = useMemo(() => {
    switch (calculationMode) {
      case CalculationMode.Merged:
        return (mergedCalculationData?.sortedTemplateIds ?? []).filter(
          id => mergedCalculationData?.templateValidity[id] !== true,
        );
      case CalculationMode.Computed:
        return templateId && computedTemplateValid !== true ? [templateId] : [];
      case CalculationMode.Manual:
        return [];
    }
  }, [calculationMode, computedTemplateValid, mergedCalculationData, templateId]);

  /**
   * One verdict from every producer: the form's own rules, the selection rules above, and the
   * badges - a template whose validator reports an error, or has not answered yet, keeps a merged
   * request from being sent. No producer overwrites another's condition.
   */
  const buttonDisabled = useMemo(() => {
    if (formHasErrors) return true;
    switch (calculationMode) {
      case CalculationMode.Merged:
        return selectionProblems.length > 0 || blockedTemplateIds.length > 0;
      case CalculationMode.Manual:
        return false;
      case CalculationMode.Computed:
        // The one template's badge gates the same way: no answer yet is not valid.
        return !templateId || blockedTemplateIds.length > 0;
    }
  }, [
    calculationMode,
    blockedTemplateIds.length,
    formHasErrors,
    selectionProblems.length,
    templateId,
  ]);

  // One request at a time: a second submission while one is pending (Enter, the button before its
  // loading state shows) would let the earlier answer replace the later one's row problems.
  const submitting = useRef(false);
  const handleSubmit = useCallback(() => {
    // The same gate for every way of submitting - the button, Enter, a context menu.
    if (buttonDisabled || submitting.current) return;
    submitting.current = true;
    setServerRowProblems([]);
    const revisionsAtSubmit = revisionsRef.current;
    form
      .validateFields()
      .then(values => {
        const request: CalculationRequest = {
          title: values.title,
          description: values.description,
          calculationMode,
          calculationType: values.private ? CalculationType.Private : CalculationType.Simulation,
          // Undefined for a non-superuser, whose form never renders the field.
          shouldImportDebugRows: values.shouldImportDebugRows ?? false,
        };

        if (calculationMode === CalculationMode.Merged) {
          request.overlappingCalculations = selectedOverlappingCalculations ?? [];

          const subOrdinateCalculations: SubordinateCalculations[] = [];

          // add all selected manual calculations (order doesn't matter)
          if (manualCalculationsData && manualCalculationsData.manualCalculationIds.length > 0) {
            manualCalculationsData.manualCalculationIds.forEach(mid => {
              subOrdinateCalculations.push({
                originalCalculationId: mid,
                title: `[manual] ${manualCalculationsData.calculationTitles[mid] ?? ''}`,
                calculationMode: CalculationMode.Manual,
                calculationType: CalculationType.Submerged,
                // A manual calculation produces no SkuPotential rows, so there is nothing to import.
                shouldImportDebugRows: false,
              });
            });
          }

          // add templates after (order doesn't matter, but keep template numbering as before)
          if (mergedCalculationData) {
            const mergedSubordinateCalculations = mergedCalculationData.sortedTemplateIds.map(
              (tid, i) => ({
                templateId: tid,
                title: `[${i + 1}] ${mergedCalculationData.calculationTitles[tid] ?? ''}`,
                calculationMode: CalculationMode.Computed,
                calculationType: CalculationType.Submerged,
                // Mirrors the root's choice. The backend applies the same rule server-side, so this
                // makes the request say what it means rather than relying on that to fill a blank.
                shouldImportDebugRows: values.shouldImportDebugRows ?? false,
              }),
            );
            subOrdinateCalculations.push(...mergedSubordinateCalculations);
          }

          if (subOrdinateCalculations.length > 0) {
            request.subordinateCalculations = subOrdinateCalculations;
          }
        } else if (calculationMode === CalculationMode.Computed) {
          request.overlappingCalculations = selectedOverlappingCalculations ?? [];
          request.templateId = templateId;
        }

        return createCalculation(request)
          .unwrap()
          .then(() => {
            form.resetFields();
            const path = generatePath(ROUTES.calculations, { projectShortName });
            debugLog('Should redirect to path:', path);
            navigate(path);
            onCancel();
          })
          .catch((error: unknown) => {
            // A problem the server names on a form field replaces the local text under that field; one
            // it names on a subordinate row the request had is reconciled with that row's local copy;
            // everything else - the root, a row the request did not send - goes to the toast.
            const sorted = sortRefusal(
              problemsOfError(error),
              rows,
              fieldOfPath,
              revisionsAtSubmit,
            );
            form.setFields(sorted.fields);
            // A row problem about a row edited or removed since the request was sent is moot
            const kept = stillRefused(sorted.rows, rowsRef.current, revisionsRef.current);
            setServerRowProblems(kept);
            if (sorted.fields.length === 0 && kept.length === 0) {
              dispatch(
                addToast({
                  message: `Calculation was not created: ${problemMessagesOf(error, 'the request was refused')}`,
                  isError: true,
                }),
              );
            } else if (sorted.other.length > 0) {
              dispatch(addToast({ message: sorted.other.join(' '), isError: true }));
            }
          });
      })
      .catch(() => {
        // form validation failed
        dispatch(
          addToast({
            message: 'Please check required fields.',
            isError: true,
          }),
        );
      })
      .finally(() => {
        submitting.current = false;
      });
  }, [
    buttonDisabled,
    rows,
    form,
    calculationMode,
    createCalculation,
    selectedOverlappingCalculations,
    manualCalculationsData,
    mergedCalculationData,
    templateId,
    projectShortName,
    navigate,
    onCancel,
    dispatch,
  ]);

  const title = Form.useWatch('title', form);

  const prefillCalculationData = useCallback(() => {
    getCalculationsQuery({
      calculationOwner: user?.id,
      showOwnersCalculationsOnly: true,
      top: 1,
      skip: 0,
      sortings: [
        {
          fieldName: 'Created',
          direction: SortDirection.Desc,
        },
      ],
    })
      .unwrap()
      .then(calculations => {
        const lastUserCalculation = calculations.data[0];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- can be null if user had no calculations before
        if (!lastUserCalculation) return;
        form.setFieldValue('title', lastUserCalculation.title);
        form.setFieldValue('description', lastUserCalculation.description ?? '');
      })
      .catch(() => {
        addToast({
          message: 'Unable to load the last calculation.',
          isError: true,
        });
      });
  }, [form, getCalculationsQuery, user?.id]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    event => {
      if (event.key === 'Enter') {
        handleSubmit();
      } else if (event.key === 'ArrowRight' && !title) {
        prefillCalculationData();
      }
    },
    [handleSubmit, prefillCalculationData, title],
  );

  useEffect(() => {
    debugLog('useEffect', inputRef.current);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  //prevent opening the calculation when used in context menu
  const onModalCancel = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onCancel();

      //clear overlapping selections
      dispatch(resetSelectedRowKeys(OVERLAP_TABLE_ID));
    },
    [dispatch, onCancel],
  );

  //prevent opening the calculation when used in context menu
  const onModalConfirm = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      handleSubmit();
    },
    [handleSubmit],
  );

  const templateDefaultValues = useMemo(() => {
    if (initialValues?.mergedCalculationTemplates) {
      const sortedTemplateIds = initialValues.mergedCalculationTemplates.map(t => t.id);
      const calculationTitles = initialValues.mergedCalculationTemplates.reduce(
        (acc, t) => ({ ...acc, [t.id]: t.title }),
        {},
      );
      return { sortedTemplateIds, calculationTitles, templateValidity: {} };
    }
    return undefined;
  }, [initialValues]);

  /**
   * We need to check for validation errors on every field change, so we can disable
   * the OK/Submit button
   */
  const onFormFieldsChange = useCallback(() => {
    setFormHasErrors(form.getFieldsError().some(({ errors }) => errors.length > 0));
  }, [form]);

  return (
    <div onClick={voidClickHandler}>
      <Modal
        data-type="modal"
        title="Create calculation"
        open={open}
        width={1000}
        okText="Create"
        okButtonProps={{
          disabled: buttonDisabled,
        }}
        onOk={onModalConfirm}
        onCancel={onModalCancel}
        confirmLoading={createCalculationStatus.isLoading}
      >
        <Overlay
          active={loading}
          overlayContent={<LoadingIndicator text="Loading last calculation..." />}
        >
          <Form
            data-type="create-calculation-form"
            form={form}
            labelCol={formLayout.labelCol}
            wrapperCol={formLayout.wrapperCol}
            name="create_calculation_modal"
            layout="horizontal"
            initialValues={initialValues}
            onFieldsChange={onFormFieldsChange}
            style={formStyle}
          >
            <Form.Item
              name="title"
              label="Title"
              rules={titleInputRules}
              extra={
                (title === '' || !title) && (
                  <InputNote onClick={prefillCalculationData}>
                    Prefill last calculation name and description with{' '}
                    <KeyboardShortcut>
                      <TbArrowBigRight />
                    </KeyboardShortcut>
                  </InputNote>
                )
              }
            >
              <Input ref={inputRef} onKeyDown={handleKeyDown} />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <TextArea rows={4} />
            </Form.Item>
            {user?.isSuperUser && (
              <Form.Item label="Type" name="private" valuePropName="checked">
                <Checkbox>Private calculation</Checkbox>
              </Form.Item>
            )}
            {user?.isSuperUser && (
              <Form.Item
                label="Debug records"
                name="shouldImportDebugRows"
                valuePropName="checked"
                extra={<InputNote>Enabled silently when any request list is configured.</InputNote>}
              >
                <Checkbox>Import debug records</Checkbox>
              </Form.Item>
            )}

            <Form.Item label="Mode" name="calculationMode">
              <CalculationModeSelect
                value={calculationMode}
                onChange={setCalculationMode}
                includeManualCalculation={user?.isSuperUser}
              />
            </Form.Item>
            {calculationMode === CalculationMode.Computed && (
              <Form.Item label="Template" name="templateId" rules={templateSelectRules}>
                {displayTemplateSelect ? (
                  <Row $gap="2rem" $alignItems="center">
                    <TemplateSelect onChange={onTemplateIdChange} templateId={templateId} />
                    {templateId && (
                      <Validator
                        key={templateId}
                        templateOrCalculation={createTemplateOrCalculationWithValue(
                          createTemplateOrCalculationObject('Template', templateId),
                        )}
                        onValidChange={setComputedTemplateValid}
                        size="small"
                      />
                    )}
                  </Row>
                ) : (
                  <Row $gap="2rem" $alignItems="center">
                    <Text>
                      #{selectedTemplate?.id} {selectedTemplate?.title}
                    </Text>
                    {templateId ? (
                      <Validator
                        key={templateId}
                        templateOrCalculation={createTemplateOrCalculationWithValue(
                          createTemplateOrCalculationObject('Template', templateId),
                        )}
                        onValidChange={setComputedTemplateValid}
                        size="small"
                      />
                    ) : null}
                  </Row>
                )}
              </Form.Item>
            )}
            {calculationMode === CalculationMode.Merged && (
              <>
                <Form.Item label="Manual Calculations">
                  {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- this is correct */}
                  {((manualCalculationsData && hasInitialManualCalculations) ||
                    !hasInitialManualCalculations) && (
                    <ManualCalculationSelect
                      onChange={setManualCalculationsData}
                      defaultValues={manualCalculationsData}
                    />
                  )}
                </Form.Item>
                <Form.Item label="Templates">
                  <MergedCalculationTemplateSelect
                    onChange={setMergedCalculationData}
                    defaultValues={templateDefaultValues}
                    serverErrors={subordinateErrors}
                  />
                  {subordinateProblems.map(p => (
                    <Text
                      key={p.key}
                      $type={p.path === SUBORDINATES_PATH ? 'warning' : 'danger'}
                      data-testid="merged-selection-problem"
                    >
                      {p.message}
                      {rowLabel(rows, p.path) ? ` (${rowLabel(rows, p.path)})` : ''}
                    </Text>
                  ))}
                </Form.Item>
              </>
            )}

            {blockedTemplateIds.length > 0 && (
              <Form.Item label="Validation">
                <Text $type="danger" role="status">
                  Validation must pass before creating this calculation. Check the Validator for:{' '}
                  {blockedTemplateIds
                    .map(id => {
                      const templateTitle = templates?.find(template => template.id === id)?.title;
                      return templateTitle ? `#${id} ${templateTitle}` : `#${id}`;
                    })
                    .join(', ')}
                  .
                </Text>
              </Form.Item>
            )}

            {calculationMode !== CalculationMode.Manual && (
              <Form.Item name="overlappingCalculations" label="Overlapping">
                <OverlappingCalculationForm
                  initialValues={
                    initialValues?.overlappingCalculations ?? initialOverlappingCalculations?.data
                  }
                />
              </Form.Item>
            )}
          </Form>
        </Overlay>
      </Modal>
    </div>
  );
};

export default CreateCalculationModal;
